import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { api, formatEuro, STUDIO_LABELS, STUDIO_SUBTITLES, STUDIO_LOGOS } from "@/lib/api";
import { SITE, IS_STUDIO, SITE_STUDIO_TYPE } from "@/config";
import { useContactPhone } from "@/components/SocialLinks";
import {
  Mic,
  Camera,
  Image as ImageIcon,
  Video,
  Radio,
  Briefcase,
  Disc3,
  Layers,
  Crown,
  Wand2,
  SlidersHorizontal,
  CalendarDays,
  Clock,
  ShieldCheck,
  ArrowRight,
  Check,
  GraduationCap,
  MapPin,
  Mail,
  Phone,
  Music,
  PenLine,
  Lightbulb,
  Info,
} from "lucide-react";

const SERVICE_ICONS = {
  mic: Mic,
  camera: Camera,
  image: ImageIcon,
  video: Video,
  radio: Radio,
  briefcase: Briefcase,
  disc: Disc3,
  layers: Layers,
  crown: Crown,
  wand: Wand2,
  sliders: SlidersHorizontal,
  music: Music,
  pen: PenLine,
  lightbulb: Lightbulb,
};

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4, ease: [0.2, 0.8, 0.2, 1] },
};

const POST_PROD_SLUGS = ["mixage", "mastering", "mix-master"];

const PriceRow = ({ svc }) => {
  if (svc.price_tiers) {
    const tiers = Object.keys(svc.price_tiers)
      .map(Number)
      .sort((a, b) => a - b);
    return (
      <div>
        <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-5" data-testid={`price-grid-${svc.slug}`}>
          {tiers.map((k) => (
            <div
              key={k}
              className="rounded-md border border-primary/25 bg-[hsl(var(--studio-yellow)/0.08)] px-1.5 py-1 text-center"
            >
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{k}h</p>
              <p className="font-mono-nums text-xs font-semibold text-primary">
                {svc.price_tiers[String(k)]}€
              </p>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          Maximum réservable : 20h. Les sessions peuvent se poursuivre après minuit (ex : 22:00 →
          05:00). Même tarif en ligne ou sur place.
        </p>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between rounded-md bg-[hsl(var(--studio-yellow)/0.10)] px-3 py-2">
      <span className="text-xs font-medium uppercase tracking-wide text-primary">Tarif</span>
      <span className="font-mono-nums font-semibold text-primary">
        {svc.from_price ? "dès " : ""}
        {formatEuro(svc.price_onsite)}
        {svc.pricing_type === "hourly" ? "/h" : ""}
        <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">en ligne ou sur place</span>
      </span>
    </div>
  );
};

export default function Home() {
  const { phone: contactPhone, href: contactPhoneHref } = useContactPhone();
  const [services, setServices] = useState([]);
  const [discount, setDiscount] = useState(10);
  const location = useLocation();

  useEffect(() => {
    api.get("/services").then((res) => setServices(res.data)).catch(() => {});
    api
      .get("/settings/public")
      .then((res) => {
        setDiscount(res.data.online_discount_percent);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth" });
      }, 150);
    }
  }, [location.hash, services.length]);

  const studioServices = services.filter((s) => s.category === "studio");
  const complements = services.filter((s) => s.category === "complement");
  const beatCatalogue = services.filter((s) => s.category === "beatmaking_catalogue");

  return (
    <div>
      {/* HERO */}
      <section className="noise-bg hero-signal border-b border-border">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-12">
            <motion.div
              className="md:col-span-7"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <Badge variant="outline" className="border-primary/40 text-primary">
                Ouvert 24h/24 — 7j/7
              </Badge>
              <h1 className="mt-5 font-heading text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
                {SITE.heroTitleA} <span className="text-primary">{SITE.heroTitleAccentA}</span>
                <br />
                {SITE.heroTitleB}
                <br />
                <span className="text-primary">{SITE.heroTitleAccentB}</span>
              </h1>
              <p className="mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
                <span className="font-medium text-foreground">{SITE.fullName}</span> — {SITE.heroText}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button size="lg" asChild data-testid="hero-booking-cta">
                  <Link to="/reserver">
                    <CalendarDays className="mr-2 h-5 w-5" />
                    Réserver un créneau
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild data-testid="hero-planning-cta">
                  <Link to="/planning">Voir le planning</Link>
                </Button>
              </div>
              <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" /> 24h/24 – 7j/7
                </span>
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-primary" /> Paiement sécurisé
                </span>
                <a href={contactPhoneHref} className="flex items-center gap-2 hover:text-primary" data-testid="hero-phone-link">
                  <Phone className="h-4 w-4 text-primary" /> {contactPhone}
                </a>
              </div>
              <div
                className="mt-6 inline-flex items-center gap-2.5 rounded-lg border border-primary/40 bg-[hsl(var(--studio-yellow)/0.08)] px-4 py-3"
                data-testid="studio-address"
              >
                <MapPin className="h-5 w-5 flex-shrink-0 text-primary" />
                <span className="text-sm font-semibold sm:text-base">
                  47 avenue de la République, 69200 Vénissieux
                </span>
              </div>
            </motion.div>
            <motion.div
              className="md:col-span-5"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <div className="overflow-hidden rounded-xl border-2 border-primary/30">
                <img src={SITE.heroImg} alt={SITE.heroAlt} className="aspect-[4/3] w-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ESPACE DU SITE */}
      <section className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <motion.div {...fadeUp}>
          <h2 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
            Un espace dédié, un planning en créneaux d'une heure
          </h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Consultez les disponibilités en temps réel et réservez votre créneau, 24h/24 – 7j/7.
          </p>
        </motion.div>
        <div className="mt-8">
          <motion.div {...fadeUp}>
            <Card
              className="group h-full overflow-hidden border-border bg-card transition-colors duration-200 hover:border-primary/50"
              data-testid={`space-card-${SITE_STUDIO_TYPE}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="relative h-56 overflow-hidden bg-black md:h-full">
                  <img
                    src={SITE.logo}
                    alt={`Logo ${SITE.fullName}`}
                    className="mx-auto h-full object-contain py-3 transition-transform duration-300 group-hover:scale-[1.04]"
                    data-testid={`space-logo-${SITE_STUDIO_TYPE}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-4 flex items-center gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary">
                      {IS_STUDIO ? (
                        <Mic className="h-5 w-5 text-primary-foreground" />
                      ) : (
                        <Camera className="h-5 w-5 text-primary-foreground" />
                      )}
                    </span>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-white">{SITE.fullName}</h3>
                      <p className="text-xs text-white/70">{SITE.subtitle}</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-5 sm:p-6">
                  <ul className="space-y-2">
                    {studioServices.map((svc) => (
                      <li key={svc.id} className="flex items-center justify-between text-sm">
                        <span className="text-foreground/90">{svc.name}</span>
                        <span className="font-mono-nums text-primary">
                          dès {formatEuro(svc.price_online)}
                          {svc.pricing_type === "hourly" ? "/h" : ""}
                        </span>
                      </li>
                    ))}
                    {IS_STUDIO && (
                      <li className="flex items-center justify-between text-sm">
                        <a href="#beatmaking" className="text-primary underline-offset-2 hover:underline">
                          Beatmaking : licences &amp; exclusivités
                        </a>
                        <ArrowRight className="h-4 w-4 text-primary" />
                      </li>
                    )}
                  </ul>
                  <div className="mt-5 flex gap-3">
                    <Button asChild className="flex-1" data-testid={`space-book-${SITE_STUDIO_TYPE}`}>
                      <Link to="/reserver">
                        Réserver
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="outline" asChild data-testid={`space-planning-${SITE_STUDIO_TYPE}`}>
                      <Link to="/planning">Planning</Link>
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* TARIFS */}
      <section id="tarifs" className="border-y border-border bg-[hsl(var(--studio-ink))] noise-bg">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
              Tarifs &amp; services
            </h2>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Payez en ligne à l'avance pour confirmer immédiatement votre créneau, ou réservez et
              réglez sur place (espèces ou carte bancaire) — <span className="text-primary">même tarif</span>.
            </p>
          </motion.div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {studioServices.map((svc) => {
              const Icon = SERVICE_ICONS[svc.icon] || Mic;
              return (
                <motion.div key={svc.id} {...fadeUp}>
                  <Card
                    className="h-full border-border bg-card transition-colors duration-200 hover:border-primary/50"
                    data-testid={`service-card-${svc.slug}`}
                  >
                    <CardContent className="flex h-full flex-col p-5 sm:p-6">
                      <div className="flex items-start justify-between">
                        <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                          <Icon className="h-5 w-5 text-primary" />
                        </span>
                        <Badge variant="outline" className="border-border text-muted-foreground">
                          {STUDIO_LABELS[svc.studio_type]}
                        </Badge>
                      </div>
                      <h3 className="mt-4 font-heading text-lg font-semibold">{svc.name}</h3>
                      <p className="mt-1.5 flex-1 text-sm text-muted-foreground">{svc.description}</p>
                      <div className="mt-5">
                        <PriceRow svc={svc} />
                      </div>
                      {svc.pricing_type === "fixed" && svc.duration_hours && (
                        <p className="mt-3 text-xs text-muted-foreground">Durée : {svc.duration_hours}h</p>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* POST-PRODUCTION & ACCOMPAGNEMENT MUSICAL — compléments de session */}
      {IS_STUDIO && [
        {
          id: "post-production",
          title: "Post-production",
          subtitle: "Mixage et mastering professionnels pour finaliser vos morceaux.",
          items: complements.filter((s) => POST_PROD_SLUGS.includes(s.slug)),
        },
        {
          id: "accompagnement",
          title: "Accompagnement musical",
          subtitle: "Topline, ghostwriting et conseils pour aller au bout de votre idée.",
          items: complements.filter((s) => !POST_PROD_SLUGS.includes(s.slug)),
        },
      ].map((section) =>
        section.items.length > 0 ? (
          <section
            key={section.id}
            id={section.id}
            className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8"
          >
            <motion.div {...fadeUp}>
              <Badge variant="outline" className="border-primary/40 text-primary">
                Compléments de session
              </Badge>
              <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
                {section.title}
              </h2>
              <p className="mt-2 max-w-2xl text-muted-foreground">{section.subtitle}</p>
              <div
                className="mt-5 flex items-start gap-3 rounded-xl border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] p-4"
                data-testid={`complements-notice-${section.id}`}
              >
                <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                <p className="text-sm text-foreground/90">
                  <span className="font-semibold text-primary">Important :</span> ces prestations sont
                  des <span className="font-medium">compléments</span> — elles se réservent uniquement
                  avec une <span className="font-medium">session d'enregistrement d'1h minimum</span>,
                  payables en ligne comme sur place.
                </p>
              </div>
            </motion.div>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {section.items.map((svc) => {
                const Icon = SERVICE_ICONS[svc.icon] || Mic;
                return (
                  <motion.div key={svc.id} {...fadeUp}>
                    <Card
                      className="h-full border-border bg-card transition-colors duration-200 hover:border-primary/50"
                      data-testid={`complement-card-${svc.slug}`}
                    >
                      <CardContent className="flex h-full flex-col p-5 sm:p-6">
                        <div className="flex items-start justify-between">
                          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                            <Icon className="h-5 w-5 text-primary" />
                          </span>
                          <Badge variant="outline" className="border-primary/40 text-[11px] text-primary">
                            Avec une session
                          </Badge>
                        </div>
                        <h3 className="mt-4 font-heading text-base font-semibold">{svc.name}</h3>
                        <p className="mt-1 flex-1 text-xs text-muted-foreground">{svc.description}</p>
                        <p className="mt-4 font-mono-nums text-xl font-semibold text-primary">
                          {formatEuro(svc.price_onsite)}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
            <motion.div {...fadeUp} className="mt-6">
              <Button asChild data-testid={`complements-booking-cta-${section.id}`}>
                <Link to="/reserver">
                  <CalendarDays className="mr-2 h-4 w-4" />
                  Réserver une session + compléments
                </Link>
              </Button>
            </motion.div>
          </section>
        ) : null
      )}

      {/* BEATMAKING (site studio uniquement) */}
      {IS_STUDIO && (
      <section id="beatmaking" className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <motion.div {...fadeUp}>
          <Badge variant="outline" className="border-primary/40 text-primary">
            {SITE.fullName}
          </Badge>
          <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
            Beatmaking : catalogue &amp; sur mesure
          </h2>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Des prods exclusives disponibles au studio et des compositions sur mesure, à des tarifs
            plus accessibles que le marché — sans compromis sur la qualité.
          </p>
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] p-4" data-testid="beatmaking-solfege-banner">
            <GraduationCap className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
            <p className="text-sm text-foreground/90">
              <span className="font-semibold text-primary">Notre différence :</span> nos beatmakers
              travaillent aux côtés d'un <span className="font-medium">musicien diplômé en solfège</span>,
              pour des compositions harmoniquement irréprochables.
            </p>
          </div>
        </motion.div>

        {/* Phase A */}
        <motion.div {...fadeUp} className="mt-10">
          <h3 className="font-heading text-lg font-semibold">
            <span className="text-primary">Phase A</span> — Prods du catalogue (disponibles au studio)
          </h3>
        </motion.div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {beatCatalogue.map((svc) => {
            const Icon = SERVICE_ICONS[svc.icon] || Disc3;
            const isExclu = svc.slug === "exclusivite-instru";
            return (
              <motion.div key={svc.id} {...fadeUp}>
                <Card
                  className={`h-full bg-card transition-colors duration-200 hover:border-primary/60 ${
                    isExclu ? "border-primary/50" : "border-border"
                  }`}
                  data-testid={`beat-card-${svc.slug}`}
                >
                  <CardContent className="flex h-full flex-col p-5 sm:p-6">
                    <div className="flex items-start justify-between">
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                        <Icon className="h-5 w-5 text-primary" />
                      </span>
                      {isExclu && <Badge className="bg-primary text-primary-foreground">Exclusivité</Badge>}
                    </div>
                    <h4 className="mt-4 font-heading text-base font-semibold">{svc.name}</h4>
                    <p className="mt-1 text-xs text-muted-foreground">{svc.description}</p>
                    <ul className="mt-4 flex-1 space-y-1.5">
                      {(svc.features || []).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-xs text-foreground/85">
                          <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5">
                      <PriceRow svc={svc} />
                    </div>
                    <Button asChild size="sm" className="mt-4" data-testid={`beat-book-${svc.slug}`}>
                      <Link to="/reserver?studio=enregistrement">Réserver</Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
          {/* Production instrumentale — affichage seul, non commandable */}
          <motion.div {...fadeUp}>
            <Card
              className="h-full border-dashed border-primary/40 bg-card"
              data-testid="beat-card-production-instrumentale"
            >
              <CardContent className="flex h-full flex-col p-5 sm:p-6">
                <div className="flex items-start justify-between">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                    <Wand2 className="h-5 w-5 text-primary" />
                  </span>
                  <Badge variant="outline" className="border-primary/40 text-[11px] text-primary">
                    Sur mesure
                  </Badge>
                </div>
                <h4 className="mt-4 font-heading text-base font-semibold">Production instrumentale</h4>
                <p className="mt-1 flex-1 text-xs text-muted-foreground">
                  Composition d'une instru sur mesure par nos beatmakers, adaptée à votre univers et
                  à votre projet.
                </p>
                <p className="mt-4 font-mono-nums text-lg font-semibold text-primary">
                  De 50 € à 200 €{" "}
                  <span className="text-xs font-normal text-muted-foreground">selon projet</span>
                </p>
                <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground" data-testid="prod-instru-contact-notice">
                  Non commandable en ligne — nous contacter pour plus d'informations.
                </p>
                <Button asChild size="sm" variant="outline" className="mt-4" data-testid="prod-instru-contact-btn">
                  <Link to="/contact">Nous contacter</Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
        <motion.div {...fadeUp} className="mt-10">
          <div
            className="flex items-start gap-3 rounded-xl border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] p-4"
            data-testid="album-quote-banner"
          >
            <Mail className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
            <p className="text-sm text-foreground/90">
              <span className="font-semibold text-primary">Projet / album complet :</span>{" "}
              l'enregistrement d'un projet ou d'un album complet se fait{" "}
              <span className="font-medium">uniquement sur devis</span>, et uniquement par mail à{" "}
              <a
                href="mailto:fnk69music@gmail.com"
                className="font-medium text-primary underline-offset-2 hover:underline"
                data-testid="album-quote-email-link"
              >
                fnk69music@gmail.com
              </a>
              .
            </p>
          </div>
        </motion.div>
      </section>
      )}

      {/* COMMENT ÇA MARCHE + REGLES */}
      <section className="border-t border-border bg-[hsl(var(--studio-ink))] noise-bg">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <motion.h2 {...fadeUp} className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
            Comment ça marche
          </motion.h2>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              {
                step: "01",
                title: "Choisissez votre créneau",
                text: "Consultez le planning de chaque espace et sélectionnez la date et l'heure qui vous conviennent, 24h/24.",
              },
              {
                step: "02",
                title: "Payez en ligne ou sur place",
                text: "Le paiement en ligne confirme définitivement votre créneau. Sur place, votre demande est validée par notre équipe et confirmée par email. Même tarif dans les deux cas.",
              },
              {
                step: "03",
                title: "Présentez-vous au studio",
                text: "Vous recevez un email de confirmation avec toutes les infos. Venez avec votre projet, on s'occupe du reste.",
              },
            ].map((item) => (
              <motion.div key={item.step} {...fadeUp}>
                <div className="h-full rounded-xl border border-border bg-card p-6">
                  <span className="font-mono-nums text-sm font-semibold text-primary">{item.step}</span>
                  <h3 className="mt-3 font-heading text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{item.text}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-12 rounded-xl border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] p-8 text-center sm:p-10">
            <h3 className="font-heading text-xl font-semibold sm:text-2xl">
              Prêt à enregistrer votre prochain projet ?
            </h3>
            <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
              Réservez dès maintenant — paiement en ligne sécurisé ou sur place au studio.
            </p>
            <Button size="lg" className="mt-6" asChild data-testid="bottom-booking-cta">
              <Link to="/reserver">
                <CalendarDays className="mr-2 h-5 w-5" />
                Réserver un créneau
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
